import React, { useEffect, useRef, useState } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Helmet } from "react-helmet";
import AdminLayout from "../components/AdminLayout";
import { BASE_URLS } from "../../config/urls";
import { apiFetch } from "../../utils/api";
import "../styles/admin-documents.css";
import {BtnSpinner} from "../../components/fields/BtnSpinner";

type DocumentItem = {
    id: number;
    title: string;
    openUrl: string;      // прямая S3 (обычно presigned) ссылка
    uploading?: boolean;  // локальный флаг загрузки новой версии
};

const AdminDocuments: React.FC = () => {
    const [docs, setDocs] = useState<DocumentItem[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // модалка "Добавить документ"
    const [isModalOpen, setModalOpen] = useState(false);
    const [newTitle, setNewTitle] = useState("");
    const [newFile, setNewFile] = useState<File | null>(null);
    const [creating, setCreating] = useState(false);

    // refs для файловых инпутов в строках таблицы
    const fileInputsRef = useRef<Record<number, HTMLInputElement | null>>({});

    useEffect(() => {
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await apiFetch(`${BASE_URLS.api}/admin/documents`, {
                    method: "GET",
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data: DocumentItem[] = await res.json();
                setDocs(data);
            } catch (e: any) {
                setError("Не удалось загрузить документы");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const handleOpen = (doc: DocumentItem) => {
        window.open(doc.openUrl, "_blank", "noopener,noreferrer");
    };

    const triggerUpload = (docId: number) => {
        const input = fileInputsRef.current[docId];
        input?.click();
    };

    const handleUploadForDoc = async (doc: DocumentItem, file: File) => {
        try {
            setDocs(prev =>
                prev.map(d => (d.id === doc.id ? { ...d, uploading: true } : d))
            );

            const form = new FormData();
            form.append("file", file);

            const resp = await apiFetch(`${BASE_URLS.api}/admin/documents/${doc.id}/upload`, {
                method: "POST",
                body: form,
            });

            if (!resp.ok) {
                throw new Error("Upload failed");
            }

            const updated: DocumentItem = await resp.json();

            setDocs(prev =>
                prev.map(d => (d.id === doc.id ? { ...updated, uploading: false } : d))
            );
        } catch (e: any) {
            setDocs(prev =>
                prev.map(d => (d.id === doc.id ? { ...d, uploading: false } : d))
            );
            alert("Ошибка загрузки файла");
        }
    };

    const openModal = () => {
        setNewTitle("");
        setNewFile(null);
        setModalOpen(true);
        document.body.style.overflow = "hidden";
    };

    const closeModal = () => {
        setModalOpen(false);
        setCreating(false);
        document.body.style.overflow = "auto";
    };

    const handleAddDoc = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newTitle || !newFile) {
            alert("Заполните название и выберите файл");
            return;
        }

        try {
            setCreating(true);

            const form = new FormData();
            form.append("title", newTitle);
            form.append("file", newFile);

            const resp = await apiFetch(`${BASE_URLS.api}/admin/documents`, {
                method: "POST",
                body: form,
                credentials: "include",
            });

            if (!resp.ok) {
                throw new Error("Create failed");
            }

            const created: DocumentItem = await resp.json();

            setDocs(prev => [created, ...prev]);
            setNewTitle("");
            setNewFile(null);
            closeModal();
        } catch (_e) {
            alert("Не удалось добавить документ");
        } finally {
            setCreating(false);
        }
    };

    return (
        <AdminLayout>
            <HelmetProvider>
                <Helmet title="Документы" />
            </HelmetProvider>

            <div className="page_content documents-page">
                <section className="admin-card" style={{ marginTop: 90 }}>
                    <h1 className="admin-heading">Документы</h1>
                    <p className="admin-subtitle">
                        Управление файлами, которые используются на сайте
                        (политика конфиденциальности, пользовательское соглашение и т.п.).
                    </p>

                    <div className="documents-header">
                        <button
                            type="button"
                            className="admin-button-primary"
                            onClick={openModal}
                        >
                            + Добавить документ
                        </button>
                    </div>

                    {error && (
                        <div className="admin-error-block">
                            {error}
                        </div>
                    )}

                    <div className="admin-table-wrapper">
                        <table className="admin_table documents-table">
                            <thead>
                            <tr>
                                <th style={{ width: "60%" }}>Название</th>
                                <th style={{ width: "40%" }}>Действия</th>
                            </tr>
                            </thead>
                            <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={2} className="admin-table-loading">
                                        <span className="admin-spinner" />
                                        Загрузка документов...
                                    </td>
                                </tr>
                            )}

                            {!loading && docs.map((doc) => (
                                <tr key={doc.id}>
                                    <td>
                                        <div className="document-title">
                                            {doc.title}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="documents-actions">
                                            <button
                                                type="button"
                                                className="admin-button-secondary admin-button-secondary--ghost"
                                                onClick={() => handleOpen(doc)}
                                            >
                                                Открыть
                                            </button>

                                            <button
                                                type="button"
                                                className="admin-button-secondary"
                                                onClick={() => triggerUpload(doc.id)}
                                                disabled={doc.uploading}
                                            >
                                                {doc.uploading ? (
                                                    <BtnSpinner/>
                                                ) : (
                                                    "Загрузить новый файл"
                                                )}
                                            </button>

                                            <input
                                                ref={(el) => {
                                                    fileInputsRef.current[doc.id] = el;
                                                }}
                                                type="file"
                                                accept="application/pdf"
                                                className="documents-file-input"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) handleUploadForDoc(doc, file);
                                                    e.currentTarget.value = "";
                                                }}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))}

                            {!loading && docs.length === 0 && !error && (
                                <tr>
                                    <td colSpan={2} className="admin-table-empty">
                                        Документы не найдены. Добавьте первый документ.
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* Модалка добавления документа */}
                {isModalOpen && (
                    <div className="documents-modal-overlay" onClick={closeModal}>
                        <div
                            className="documents-modal"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h2 className="documents-modal-title">
                                Добавить документ
                            </h2>

                            <form className="documents-form" onSubmit={handleAddDoc}>
                                <div className="admin-field">
                                    <div className="admin_title">Название</div>
                                    <input
                                        type="text"
                                        className="admin-input"
                                        value={newTitle}
                                        onChange={(e) => setNewTitle(e.target.value)}
                                        placeholder="Например: Политика конфиденциальности"
                                        required
                                    />
                                </div>


                                <div className="admin-field">
                                    <div className="admin_title">Файл (PDF)</div>
                                    <input
                                        type="file"
                                        className="admin-input"
                                        style={{paddingTop: 8}}
                                        accept="application/pdf"
                                        onChange={(e) =>
                                            setNewFile(e.target.files?.[0] || null)
                                        }
                                        required
                                    />
                                </div>

                                <p className="documents-hint">
                                    После добавления документа свяжитесь с разработчиком
                                    и уточните, на какой странице сайта его разместить.
                                </p>

                                <div className="documents-modal-actions">
                                    <button
                                        type="button"
                                        className="admin-button-secondary admin-button-secondary--ghost"
                                        onClick={closeModal}
                                        disabled={creating}
                                    >
                                        Отмена
                                    </button>
                                    <button
                                        type="submit"
                                        className="admin-button-primary"
                                        disabled={creating}
                                    >
                                        {creating ? (
                                            <BtnSpinner/>
                                        ) : (
                                            "Добавить"
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default AdminDocuments;
